import { useEffect, useId, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Instagram } from "lucide-react";
import { track } from "../features/analytics/tracker";

const STORAGE_KEY = "tdh_newsletter_dismissed";
const DELAY_MS    = 12_000;

// ── Inline styles so this popup never clashes with global CSS ──────────────
const S = {
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.45)",
    zIndex: 300,
    cursor: "pointer",
  },
  panel: {
    position: "fixed",
    bottom: "1.5rem",
    left: "1rem",
    right: "1rem",
    maxWidth: "420px",
    margin: "0 auto",
    background: "#fff",
    borderRadius: "1.25rem",
    overflow: "hidden",
    zIndex: 301,
    boxShadow: "0 16px 60px rgba(0,0,0,0.22)",
    fontFamily: "inherit",
  },
  img: {
    width: "100%",
    height: "170px",
    objectFit: "cover",
    display: "block",
  },
  body: {
    padding: "1.4rem 1.5rem 1.6rem",
  },
  closeBtn: {
    position: "absolute",
    top: "0.65rem",
    right: "0.75rem",
    width: "2rem",
    height: "2rem",
    borderRadius: "50%",
    border: "none",
    background: "rgba(0,0,0,0.35)",
    color: "#fff",
    fontSize: "0.85rem",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 1,
    zIndex: 302,
  },
  eyebrow: {
    fontSize: "0.7rem",
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: "#5a8a5e",
    margin: "0 0 0.35rem",
  },
  heading: {
    fontSize: "1.3rem",
    fontWeight: 800,
    lineHeight: 1.2,
    color: "#1a2a1a",
    margin: "0 0 0.5rem",
  },
  sub: {
    fontSize: "0.85rem",
    color: "#666",
    margin: "0 0 1.1rem",
    lineHeight: 1.5,
  },
  row: {
    display: "flex",
    gap: "0.5rem",
  },
  input: (isError) => ({
    flex: 1,
    padding: "0.6rem 0.85rem",
    fontSize: "0.875rem",
    borderRadius: "0.6rem",
    border: `1.5px solid ${isError ? "#e05555" : "#d8e4d8"}`,
    outline: "none",
    background: "#f7faf7",
    color: "#1a2a1a",
    minWidth: 0,
  }),
  btn: (loading) => ({
    padding: "0.6rem 1.1rem",
    fontSize: "0.875rem",
    fontWeight: 700,
    borderRadius: "0.6rem",
    border: "none",
    background: loading ? "#8aab8e" : "#2e7d32",
    color: "#fff",
    cursor: loading ? "default" : "pointer",
    whiteSpace: "nowrap",
    flexShrink: 0,
    transition: "background 0.2s",
  }),
  msg: (isSuccess) => ({
    marginTop: "0.65rem",
    fontSize: "0.8rem",
    color: isSuccess ? "#2e7d32" : "#c0392b",
    fontWeight: 600,
  }),
  dismiss: {
    marginTop: "0.75rem",
    display: "block",
    width: "100%",
    textAlign: "center",
    fontSize: "0.75rem",
    color: "#aaa",
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 0,
  },
  social: {
    marginTop: "0.9rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.35rem",
    fontSize: "0.78rem",
    fontWeight: 600,
    color: "#5a8a5e",
    textDecoration: "none",
  },
};

function PopupForm({ onSuccess }) {
  const [email, setEmail]     = useState("");
  const [status, setStatus]   = useState("");
  const [message, setMessage] = useState("");
  const fieldId = useId();
  const hpId    = useId();

  useEffect(() => {
    if (status === "success" || status === "error") {
      const t = setTimeout(() => { setStatus(""); setMessage(""); }, 4000);
      return () => clearTimeout(t);
    }
  }, [status]);

  async function handleSubmit(e) {
    e?.preventDefault?.();
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      setStatus("error"); setMessage("Enter a valid email."); return;
    }
    if (document.getElementById(hpId)?.value) return;

    try {
      setStatus("loading");
      const res = await fetch("https://teardrophydro.co.uk/garageFarm/newsletter", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (res.ok) {
        setStatus("success");
        setMessage("You're in! 🌿");
        setEmail("");
        track?.("newsletter_signup", { source: "popup" });
        setTimeout(() => onSuccess?.(), 1800);
      } else {
        setStatus("error"); setMessage("Something went wrong. Try again.");
      }
    } catch {
      setStatus("error"); setMessage("Network error. Try again.");
    }
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      <p style={S.eyebrow}>Fresh from the farm</p>
      <h3 style={S.heading}>Be first to know what's growing</h3>
      <p style={S.sub}>New varieties, seasonal drops, and availability - straight to your inbox.</p>

      <div style={S.row}>
        <label htmlFor={fieldId} style={{ position: "absolute", opacity: 0, pointerEvents: "none" }}>
          Email address
        </label>
        <input
          id={fieldId}
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder="you@example.com"
          value={email}
          onChange={e => setEmail(e.target.value)}
          style={S.input(status === "error")}
          aria-invalid={status === "error"}
        />
        <button type="submit" disabled={status === "loading"} style={S.btn(status === "loading")}>
          {status === "loading" ? "…" : "Subscribe"}
        </button>
        {/* honeypot */}
        <input id={hpId} type="text" tabIndex="-1" autoComplete="off"
          style={{ position: "absolute", left: "-9999px", opacity: 0 }} />
      </div>

      {status && message && (
        <p style={S.msg(status === "success")}>{message}</p>
      )}
    </form>
  );
}

export default function MailingListPopup() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(STORAGE_KEY)) return;
    const id = setTimeout(() => {
      setVisible(true);
      track?.("newsletter_popup_shown", { page: "browse" });
    }, DELAY_MS);
    return () => clearTimeout(id);
  }, []);

  function dismiss() {
    sessionStorage.setItem(STORAGE_KEY, "1");
    setVisible(false);
    track?.("newsletter_popup_dismissed", { page: "browse" });
  }

  return (
    <AnimatePresence>
      {visible && (
        <>
          <motion.div
            style={S.backdrop}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={dismiss}
          />

          <motion.div
            style={S.panel}
            initial={{ opacity: 0, y: 48, scale: 0.96 }}
            animate={{ opacity: 1, y: 0,  scale: 1    }}
            exit={{    opacity: 0, y: 32, scale: 0.96 }}
            transition={{ duration: 0.38, ease: [0.16, 1, 0.3, 1] }}
          >
            {/* Close button sits over the image */}
            <button style={S.closeBtn} onClick={dismiss} aria-label="Close">✕</button>

            {/* Swap src for whichever image you choose */}
            <img
              src="/images/products/genoBasil/IMG_4565.JPEG"
              alt="Fresh basil growing"
              style={S.img}
            />

            <div style={S.body}>
              <PopupForm onSuccess={dismiss} />
              <a
                href="https://www.instagram.com/teardrop_hydro/"
                target="_blank"
                rel="noopener noreferrer"
                style={S.social}
              >
                <Instagram size={15} />
                Follow @teardrop_hydro
              </a>
              <button style={S.dismiss} onClick={dismiss}>No thanks</button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}